import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "dx-chunk-reload";

/**
 * React.lazy with resilience against stale chunk URLs after a new deploy.
 * If a dynamic import fails (old hashed file no longer on the CDN), we retry
 * once, then force a single hard reload to pick up the new index.html.
 */
export function lazyRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      sessionStorage.removeItem(RELOAD_KEY);
      return mod;
    } catch (err) {
      // Second chance: transient network hiccup.
      try {
        const mod = await factory();
        sessionStorage.removeItem(RELOAD_KEY);
        return mod;
      } catch {
        const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY) === "1";
        if (!alreadyReloaded) {
          sessionStorage.setItem(RELOAD_KEY, "1");
          // Drop stale caches so the reload fetches fresh assets.
          try {
            if ("caches" in window) {
              const keys = await caches.keys();
              await Promise.all(keys.map((k) => caches.delete(k)));
            }
          } catch {
            /* ignore */
          }
          window.location.reload();
          // Never resolves — the page is reloading.
          return new Promise<{ default: T }>(() => {});
        }
        throw err;
      }
    }
  });
}
